/**
 * Erinnerungen an Termine (Roadmap F-04 f) - rein und getestet.
 *
 * `reminderMinutes` sind Minuten vor Beginn. Ganztaegige Termine haben keine
 * Uhrzeit; dort zaehlt 8:00 Uhr des Tages als Beginn ("am Tag um 8 Uhr",
 * mit 1440 "am Vortag um 8 Uhr").
 */
import { addMinutes, datePart } from '@/lib/datetime';
import type { CalendarOccurrence, LocalDateTime } from '@/domain/types';

/** Bezugszeit ganztaegiger Termine. */
export const GANZTAGS_UHRZEIT = '08:00';

/** Auswahl in der Termin-Leiste. */
export const ERINNERUNG_OPTIONEN: { minuten: number; label: string; ganztags: string }[] = [
  { minuten: 0, label: 'Zum Beginn', ganztags: 'Am Tag, 8 Uhr' },
  { minuten: 10, label: '10 min vorher', ganztags: '' },
  { minuten: 30, label: '30 min vorher', ganztags: '' },
  { minuten: 60, label: '1 h vorher', ganztags: '' },
  { minuten: 1440, label: '1 Tag vorher', ganztags: 'Am Vortag, 8 Uhr' },
];

export interface Erinnerung {
  /** Eindeutig je Vorkommen und Vorlauf - damit jede nur einmal erscheint. */
  key: string;
  occurrence: CalendarOccurrence;
  minuten: number;
  zeit: LocalDateTime;
}

export function erinnerungsZeit(o: Pick<CalendarOccurrence, 'start' | 'allDay'>, minuten: number): LocalDateTime {
  const beginn = (o.allDay ? `${datePart(String(o.start))}T${GANZTAGS_UHRZEIT}` : String(o.start)) as LocalDateTime;
  return addMinutes(beginn, -minuten);
}

/** Gueltige Werte aus Formular oder Sicherung: ganze Minuten, 0 bis 4 Wochen, ohne Doppelte. */
export function erinnerungenBereinigt(werte: unknown): number[] {
  if (!Array.isArray(werte)) return [];
  const ok = werte.filter((m): m is number => Number.isInteger(m) && m >= 0 && m <= 40320);
  return [...new Set(ok)].sort((a, b) => a - b);
}

/**
 * Alle Erinnerungen, deren Zeitpunkt in (von, bis] liegt, aeltere zuerst.
 * Beim Oeffnen der App reicht `von` etwas zurueck, damit eine gerade
 * verpasste Erinnerung noch erscheint.
 */
export function faelligeErinnerungen(
  occurrences: CalendarOccurrence[], von: LocalDateTime, bis: LocalDateTime,
): Erinnerung[] {
  const out: Erinnerung[] = [];
  for (const o of occurrences) {
    for (const minuten of erinnerungenBereinigt(o.reminderMinutes)) {
      const zeit = erinnerungsZeit(o, minuten);
      if (zeit > von && zeit <= bis) out.push({ key: `${o.occurrenceId}|${minuten}`, occurrence: o, minuten, zeit });
    }
  }
  return out.sort((a, b) => a.zeit.localeCompare(b.zeit));
}

/**
 * "Beginnt in 10 min", "Beginnt jetzt", "Morgen ganztägig" usw. Mit `jetzt`
 * erkennt der Text, dass ein nachgeholter Termin schon laeuft.
 */
export function erinnerungsText(e: Erinnerung, jetzt?: LocalDateTime): string {
  if (e.occurrence.allDay) return e.minuten >= 1440 ? 'Morgen, ganztägig' : 'Heute, ganztägig';
  if (jetzt && String(e.occurrence.start) < jetzt) return 'Hat schon begonnen';
  if (e.minuten === 0) return 'Beginnt jetzt';
  if (e.minuten < 60) return `Beginnt in ${e.minuten} min`;
  if (e.minuten < 1440) return `Beginnt in ${e.minuten / 60 === Math.floor(e.minuten / 60) ? e.minuten / 60 : (e.minuten / 60).toFixed(1)} h`;
  return 'Beginnt morgen';
}
